import React from "react";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import EditIcon from "@mui/icons-material/Edit";
import FilterListIcon from "@mui/icons-material/FilterList";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { TrainingEmptyState } from "./TrainingEmptyState";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getRightworkAPI } from "../requests/utilities/requests";
import { formatDate } from "../requests/utilities/helpers";
import { API_get_training_badges } from "../../../constants";

const filterSelectSx = {
  height: 42,
  minWidth: 170,
  borderRadius: "8px",
  backgroundColor: "white",
  fontSize: 14,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a78bfa" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
  "& .MuiSelect-select": {
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "12px",
  },
};

function BadgeCard({ badge, onEdit }) {
  const awardingTrackCount = badge.granting_track_count || 0;
  const expirationLabel = badge.validity_days
    ? `Expires after ${badge.validity_days} days`
    : "Never expires";
  const updatedLabel = badge.updated_at
    ? `Updated ${formatDate(badge.updated_at)}`
    : null;

  return (
    <div
      className="group cursor-pointer rounded-lg border border-slate-200 bg-white p-4 transition-all duration-200 hover:border-slate-300 hover:shadow-md"
      onClick={onEdit}
    >
      <div className="flex items-start gap-3">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${badge.color || "bg-violet-600"}`}
        >
          <WorkspacePremiumIcon className="text-white" style={{ fontSize: 20 }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="line-clamp-2 text-sm font-semibold text-slate-900">
                {badge.name || "Untitled badge"}
              </h3>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                <span>{expirationLabel}</span>
                <span>·</span>
                <span>
                  {awardingTrackCount === 1
                    ? "Awarded by 1 track"
                    : `Awarded by ${awardingTrackCount} tracks`}
                </span>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
              <Tooltip title="Edit badge">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit();
                  }}
                  sx={{ color: "#475569" }}
                >
                  <EditIcon style={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          {badge.description && (
            <p className="mt-2 line-clamp-2 text-xs text-slate-500">
              {badge.description}
            </p>
          )}

          {updatedLabel && (
            <div className="mt-3 flex justify-end text-xs text-slate-400">
              {updatedLabel}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BadgesDisplay(props) {
  const { homeCompany, onCreate, onEdit } = props;
  const [badges, setBadges] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState("");
  const [expirationFilter, setExpirationFilter] = React.useState("all");
  const [sortKey, setSortKey] = React.useState("recent");

  const fetchBadges = React.useCallback(async () => {
    if (!homeCompany) return;
    setIsLoading(true);
    try {
      const res = await getRightworkAPI(API_get_training_badges, {
        company_id: homeCompany,
      });
      setBadges(res.data?.redux?.payload?.training_badges || []);
    } catch (err) {
      toast.error("Couldn't load badges. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [homeCompany]);

  React.useEffect(() => {
    fetchBadges();
  }, [fetchBadges]);

  // Archived badges are hidden here (they stay resolvable in the track
  // editor's picker so stored mappings keep an honest label). Single source
  // for "active" so the list and the empty-state heuristic can't disagree.
  const activeBadges = React.useMemo(
    () => (badges || []).filter((b) => !b.archived_at),
    [badges]
  );

  const filteredBadges = React.useMemo(() => {
    const searchParts = searchInput.toLowerCase().split(" ").filter(Boolean);
    const filtered = activeBadges.filter((badge) => {
      const haystack = `${badge.name || ""} ${badge.description || ""}`.toLowerCase();
      if (!searchParts.every((part) => haystack.includes(part))) return false;
      if (expirationFilter === "expires" && !badge.validity_days) {
        return false;
      }
      if (expirationFilter === "never_expires" && badge.validity_days) {
        return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (sortKey === "name") {
        return (a.name || "").localeCompare(b.name || "");
      }
      if (sortKey === "tracks") {
        return (b.granting_track_count || 0) - (a.granting_track_count || 0);
      }
      if (sortKey === "expiration") {
        const normalizeExpiration = (days) => days || Number.MAX_SAFE_INTEGER;
        return normalizeExpiration(b.validity_days) - normalizeExpiration(a.validity_days);
      }
      return (b.updated_at || b.created_at || 0) - (a.updated_at || a.created_at || 0);
    });
  }, [activeBadges, searchInput, expirationFilter, sortKey]);

  const activeFilterCount = expirationFilter !== "all" ? 1 : 0;
  const hasSearchOrFilters = Boolean(searchInput || activeFilterCount);

  const clearSearchAndFilters = () => {
    setSearchInput("");
    setExpirationFilter("all");
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-8">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="mb-1 text-2xl font-bold text-slate-900">Badges</h1>
              <p className="text-sm text-slate-600">
                Credentials your team earns by completing tracks, shown on their profile
              </p>
            </div>
            <Tooltip title="Create new badge">
              <button
                type="button"
                onClick={() => onCreate && onCreate()}
                className="flex items-center gap-2 rounded-lg bg-violet-700 px-6 py-3 text-white shadow-sm transition-colors hover:bg-violet-800"
              >
                <AddIcon style={{ fontSize: "20px" }} />
                <span className="font-medium">New Badge</span>
              </button>
            </Tooltip>
          </div>

          <div className="mb-6 flex items-center gap-6 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <WorkspacePremiumIcon style={{ fontSize: "18px" }} />
              <span>
                {filteredBadges.length}{" "}
                {filteredBadges.length === 1 ? "Badge" : "Badges"}
              </span>
            </div>
            {searchInput && (
              <div className="flex items-center gap-2 text-violet-600">
                <SearchIcon style={{ fontSize: "16px" }} />
                <span>Showing search results</span>
              </div>
            )}
            {activeFilterCount > 0 && (
              <div className="flex items-center gap-2 text-violet-600">
                <FilterListIcon style={{ fontSize: "16px" }} />
                <span>{activeFilterCount} filter active</span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full max-w-md">
              <SearchIcon
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                style={{ fontSize: "20px" }}
              />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search badges..."
                className="w-full rounded-lg border border-slate-200 py-2.5 pl-10 pr-4 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-100"
              />
            </div>

            <Select
              size="small"
              sx={filterSelectSx}
              value={expirationFilter}
              onChange={(event) => setExpirationFilter(event.target.value)}
            >
              <MenuItem value="all" sx={{ fontSize: 14 }}>
                All expiration
              </MenuItem>
              <MenuItem value="expires" sx={{ fontSize: 14 }}>
                Expires
              </MenuItem>
              <MenuItem value="never_expires" sx={{ fontSize: 14 }}>
                Never expires
              </MenuItem>
            </Select>

            <Select
              size="small"
              sx={filterSelectSx}
              value={sortKey}
              onChange={(event) => setSortKey(event.target.value)}
            >
              <MenuItem value="recent" sx={{ fontSize: 14 }}>
                Recently updated
              </MenuItem>
              <MenuItem value="name" sx={{ fontSize: 14 }}>
                Name
              </MenuItem>
              <MenuItem value="tracks" sx={{ fontSize: 14 }}>
                Most awarding tracks
              </MenuItem>
              <MenuItem value="expiration" sx={{ fontSize: 14 }}>
                Longest expiration
              </MenuItem>
            </Select>
          </div>

        </div>
      </div>

      <div className="px-6 py-8">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <LoadSpinner />
          </div>
        ) : filteredBadges.length === 0 ? (
          <TrainingEmptyState
            icon={WorkspacePremiumIcon}
            plural="badges"
            blurb="Create badges like Certified Trainer or Barista Level 2, map them to tracks, and your team earns them by completing the training."
            createLabel="Create your first badge"
            searching={hasSearchOrFilters}
            noResultsTitle="No badges match your filters"
            noResultsBlurb="Try adjusting your search or clearing the selected filters."
            clearLabel="Clear filters"
            onClear={hasSearchOrFilters ? clearSearchAndFilters : undefined}
            onCreate={() => onCreate && onCreate()}
          />
        ) : (
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-1">
            {filteredBadges.map((badge) => (
              <BadgeCard
                key={badge.uuid}
                badge={badge}
                onEdit={() => onEdit && onEdit(badge)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default BadgesDisplay;
